import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageBackButton from "../../../components/ui/Page/PageBackButton";
import Toast, { type ToastState } from "../../../components/ui/Toast/Toast";
import TeenBusinessListingApplicationsSection from "../../../components/teen/gigs/TeenBusinessListingApplicationsSection";
import useTeenGuardianRequirement from "../../../hooks/useTeenGuardianRequirement";
import { businessListingsApi } from "../../../lib/businessListings";
import type { TeenBusinessListingApplicationListItemResponse } from "../../../types/businessListingTypes";
import { apiErrorToMessage } from "../../../utils/ApiUtils";
import { buildGuardianSetupHref } from "../../../utils/GuardianSetupUtils";

const TeenBusinessApplicationsPage = () => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState<TeenBusinessListingApplicationListItemResponse[]>([]);
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingWithdrawApplicationId, setPendingWithdrawApplicationId] = useState<string | null>(null);
  const [pendingReapplyApplicationId, setPendingReapplyApplicationId] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastState>(null);
  const teenGuardianRequirement = useTeenGuardianRequirement();

  useEffect(() => {
    let cancelled = false;

    const loadApplications = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await businessListingsApi.listApplicationsForTeen();
        if (!cancelled) {
          setApplications(response);
        }
      } catch (requestError) {
        if (!cancelled) {
          setError(apiErrorToMessage(requestError, "Failed to load your business applications."));
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void loadApplications();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleWithdrawApplication = async (applicationId: string) => {
    setPendingWithdrawApplicationId(applicationId);
    setError(null);
    try {
      const response = await businessListingsApi.withdrawApplicationAsTeen(applicationId);
      setApplications((currentApplications) =>
        currentApplications.map((application) =>
          application.applicationId === applicationId
            ? {
                ...application,
                applicationStatus: response.status,
                applicationMessage: response.message,
                updatedAt: response.updatedAt,
                resume: response.resume,
              }
            : application,
        ),
      );
      setToast({ tone: "success", message: "Business application withdrawn." });
    } catch (requestError) {
      const message = apiErrorToMessage(requestError, "Failed to withdraw your business application.");
      setError(message);
      setToast({ tone: "error", message });
      throw requestError;
    } finally {
      setPendingWithdrawApplicationId(null);
    }
  };

  const handleReapplyToBusinessListing = async (applicationId: string) => {
    const application = applications.find((currentApplication) => currentApplication.applicationId === applicationId);
    if (!application?.listingId) {
      const message = "This listing is no longer available to reapply.";
      setError(message);
      setToast({ tone: "error", message });
      return;
    }

    if (teenGuardianRequirement.requiresGuardian) {
      if (teenGuardianRequirement.isLoading || teenGuardianRequirement.isFetching) {
        setToast({ tone: "info", message: "Checking your guardian setup. Try again in a second." });
        return;
      }

      if (teenGuardianRequirement.requiresSetup) {
        navigate(buildGuardianSetupHref("/teen/business-applications"));
        return;
      }
    }

    setPendingReapplyApplicationId(applicationId);
    setError(null);
    try {
      const response = await businessListingsApi.applyAsTeen(application.listingId, {});
      setApplications((currentApplications) =>
        currentApplications.map((currentApplication) =>
          currentApplication.applicationId === applicationId
            ? {
                ...currentApplication,
                applicationStatus: response.status,
                applicationMessage: response.message,
                updatedAt: response.updatedAt,
                resume: response.resume,
              }
            : currentApplication,
        ),
      );
      setToast({ tone: "success", message: "Application submitted again." });
    } catch (requestError) {
      const message = apiErrorToMessage(requestError, "Failed to submit your application again.");
      if (message.includes("Add a parent or guardian before applying for gigs")) {
        navigate(buildGuardianSetupHref("/teen/business-applications"));
        return;
      }
      setError(message);
      setToast({ tone: "error", message });
    } finally {
      setPendingReapplyApplicationId(null);
    }
  };

  return (
    <main className="mx-auto max-w-6xl px-2 py-6 sm:px-6 sm:py-8">
      <Toast
        isOpen={Boolean(toast)}
        message={toast?.message ?? ""}
        tone={toast?.tone}
        onClose={() => setToast(null)}
      />
      <PageBackButton fallbackTo="/dashboard" label="Back to Dashboard" className="mb-2" />
      <section className="overflow-hidden rounded-[2rem] border border-myteengig-border bg-[radial-gradient(circle_at_top_left,_rgba(163,230,53,0.24),_transparent_32%),linear-gradient(135deg,_rgba(255,255,255,1),_rgba(244,247,240,1))] shadow-sm">
        <div className="grid gap-8 px-3 py-6 sm:px-6 sm:py-8 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,0.7fr)] lg:px-8">
          <div className="lg:col-span-2">
            <div className="mx-auto flex max-w-3xl flex-col items-center text-center">
              <h1 className="mt-3 text-3xl font-bold tracking-tight text-myteengig-text-primary sm:text-4xl">Business Applications</h1>
              <p className="mt-4 text-base leading-7 text-myteengig-text-muted">
                Job applications that you have submitted for businesses.
              </p>
            </div>
          </div>
        </div>
      </section>

      {isLoading ? <p className="mt-6 text-myteengig-text-muted">Loading your business applications...</p> : null}
      {error ? <p className="mt-6 text-sm font-medium text-red-600">{error}</p> : null}
      {!isLoading && !error ? (
        <TeenBusinessListingApplicationsSection
          applications={applications}
          search={search}
          onSearchChange={setSearch}
          onWithdrawApplication={handleWithdrawApplication}
          pendingWithdrawApplicationId={pendingWithdrawApplicationId}
          onReapplyApplication={handleReapplyToBusinessListing}
          pendingReapplyApplicationId={pendingReapplyApplicationId}
        />
      ) : null}
    </main>
  );
};

export default TeenBusinessApplicationsPage;
